
import { createFileRoute, Navigate, type SearchSchemaInput } from '@tanstack/react-router';
import { useApiQuery } from '../hooks/useApi';
import type { ApiAttempt } from '../api/simulations.api';
import { CollectionType, type Collection } from '@/types/collection';
import { attemptKeys } from '../hooks/attempts/useAttempts';
import SimulationCorrection from '../components/SimulationFlow/SimulationCorrection';
import Loader from '../components/Loader/Loader';
import GeneralError from '../components/Errors/General';

export const Route = createFileRoute('/simulazioni/$id_/correzione')({
  validateSearch: (search: { from?: string } & SearchSchemaInput): { from: string } => ({
    from: search.from ?? 'simulazioni',
  }),
  component: RouteComponent,
});

function toCorrectionCollection(attempt: ApiAttempt): Collection {
  return {
    _id: attempt.collectionId,
    name: attempt.collection?.name ?? '',
    type: (attempt.collection?.type as CollectionType) ?? CollectionType.SIMULATION,
    attributes: {},
    status: '',
    archived: false,
    timed: attempt.collection?.timed ?? false,
    enableCorrection: attempt.collection?.enableCorrection,
    createdAt: '',
    updatedAt: '',
  };
}

function RouteComponent() {
  const { id } = Route.useParams();
  const { from } = Route.useSearch();
  const backPath = `/${from}`;

  const { data: attempt, isFetching: isFetchingAttempt, error: attemptError } = useApiQuery<ApiAttempt>(
    `/attempts/${id}`,
    attemptKeys.detail(id),
    undefined,
    { enabled: !!id, staleTime: 60000, gcTime: 60000 },
  );

  if (isFetchingAttempt) return <Loader fullScreen />;
  if (attemptError || !attempt) return <GeneralError />;

  // The collection comes with the attempt (not from GET /collections/{id}, which
  // only returns ACTIVE collections): a completed attempt stays viewable even if
  // its collection is now draft or inactive.
  const collection = toCorrectionCollection(attempt);
  if (collection.enableCorrection === false) return <Navigate to={backPath} replace />;

  return (
    <SimulationCorrection
      attempt={attempt}
      collection={collection}
      backPath={backPath}
    />
  );
}
